"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle, MessageSquare, Globe, Bot, BarChart3, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslations } from 'next-intl';

const PriceComparisonSection: React.FC = () => {
  const [showAllFeatures, setShowAllFeatures] = useState<boolean>(false);
  const t = useTranslations('mdnqontak.comparison');
  
  // Get plan names from translations
  const planNames = t.raw('plans');
  const categories = t.raw('categories');
  const features = t.raw('features');

  return (
    <section className="py-20 bg-white dark:bg-slate-900">
      <div className="container px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
            {t('title')} <span className="text-blue-600 dark:text-blue-400">{t('titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-xl">
            {/* Table Header */}
            <div className="grid grid-cols-5 gap-4 p-6 bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-slate-700 rounded-t-2xl border-b dark:border-slate-600">
              <div className="font-semibold text-slate-900 dark:text-white">{t('mobile.subtitle')}</div>
              <div className="text-center">
                <div className="font-bold text-lg text-slate-900 dark:text-white">{planNames.broadcast}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">IDR 750.000/month</div>
              </div>
              <div className="text-center relative">
                <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs">
                  Popular
                </Badge>
                <div className="font-bold text-lg text-slate-900 dark:text-white mt-4">{planNames.service}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">IDR 400.000/user/month</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-slate-900 dark:text-white">{planNames.sales}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">IDR 400.000/user/month</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-slate-900 dark:text-white">{planNames.enterprise}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Custom Pricing</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y dark:divide-slate-700">
              {/* WhatsApp Business API */}
              <div className="p-6">
                <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.whatsappApi}</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-5 gap-4 items-center py-2">
                    <div className="text-slate-700 dark:text-slate-300">{features.whatsappBlueTick}</div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-4 items-center py-2">
                    <div className="text-slate-700 dark:text-slate-300">{features.waMasking}</div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-4 items-center py-2">
                    <div className="text-slate-700 dark:text-slate-300">{features.whatsappCall}</div>
                    <div className="text-center">
                      <span className="text-red-500 text-lg">✗</span>
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-4 items-center py-2">
                    <div className="text-slate-700 dark:text-slate-300">{features.whatsappGroup}</div>
                    <div className="text-center">
                      <span className="text-red-500 text-lg">✗</span>
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                    <div className="text-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Channel Integration */}
              <div className="p-6">
                <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.channelIntegration}</h3>
                <div className="space-y-3">
                  {[features.instagram, features.facebook, features.line, features.twitter, features.telegram, features.googleMyBusiness].map((channel: string, idx: number) => (
                    <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                      <div className="text-slate-700 dark:text-slate-300">{channel}</div>
                      <div className="text-center">
                        <span className="text-red-500 text-lg">✗</span>
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                    </div>
                  ))}
                  <div className="grid grid-cols-5 gap-4 items-center py-2">
                    <div className="text-slate-700 dark:text-slate-300">{features.ecommerce}</div>
                    <div className="text-center">
                      <span className="text-red-500 text-lg">✗</span>
                    </div>
                    <div className="text-center">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Unlimited</span>
                    </div>
                    <div className="text-center">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Unlimited</span>
                    </div>
                    <div className="text-center">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Unlimited</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Broadcast Features */}
              <div className="p-6">
                <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.broadcast}</h3>
                <div className="space-y-3">
                  {[features.emailTemplate, features.emailBroadcast, features.whatsappBroadcastTemplate, features.whatsappBroadcast].map((feature: string, idx: number) => (
                    <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                      <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                    </div>
                  ))}
                  {[features.dailyScheduling, features.customScheduling].map((feature: string, idx: number) => (
                    <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                      <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                      <div className="text-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Show More Button */}
              <div className="p-6 text-center bg-slate-50 dark:bg-slate-800">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAllFeatures(!showAllFeatures)}
                  className="mb-4 dark:border-slate-600 dark:text-slate-300"
                >
                  {showAllFeatures ? (
                    <>
                      <ChevronUp className="mr-2 h-4 w-4" />
                      {t('showLess')}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="mr-2 h-4 w-4" />
                      {t('showMore')}
                    </>
                  )}
                </Button>
              </div>

              {/* Expanded Features - Show when showAllFeatures is true */}
              {showAllFeatures && (
                <>
                  {/* AI Features */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.aiChatbot}</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.basicChatbot}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.commerceChatbot}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.nlpIntent}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.conversationalAi}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.chatSummarization}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.paraphrase}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Agent Monitoring */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.agentMonitoring}</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.slaManagementChat}</div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.ticketSlaCountdown}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.chatSlaCountdown}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.scorecardManual}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.scorecardAutomated}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.gpsLiveTracking}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CRM & Sales Management */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.crmSales}</h3>
                    <div className="space-y-3">
                      {[features.dealPipeline, features.createNewDeal, features.uploadMultipleDeal, features.bulkChangeDeals, features.advancedRulesApprover].map((feature: string, idx: number) => (
                        <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                          <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Task Management */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.taskManagement}</h3>
                    <div className="space-y-3">
                      {[features.addTask, features.uploadTask, features.downloadTask, features.applyFilterTask, features.bulkChangeOwnership, features.deleteTask].map((feature: string, idx: number) => (
                        <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                          <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                        </div>
                      ))}
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.associateTaskToDeal}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.associateTaskToTicket}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ticket Management */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.ticketManagement}</h3>
                    <div className="space-y-3">
                      {[features.createTicket, features.editTicket, features.applyFilterTicket, features.downloadTicket, features.ticketingPipeline].map((feature: string, idx: number) => (
                        <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                          <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Knowledge Base */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.knowledgeBase}</h3>
                    <div className="space-y-3">
                      {[features.knowledgeContentManagement, features.knowledgeIntegrationToChat, features.articleBulkUpload, features.articleVisibilitySegregation].map((feature: string, idx: number) => (
                        <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                          <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Advanced Reports */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.reports}</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.emailWhatsappBroadcastReport}</div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      {[features.chatGeneralReport, features.agentPerformance, features.botPerformance, features.contactReportChat, features.csatNpsCesChat].map((feature: string, idx: number) => (
                        <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                          <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                        </div>
                      ))}
                      {[features.crmGeneralReport, features.targetingReportCrm, features.geographicReportCrm, features.trendReportCrm, features.dashboardReport].map((feature: string, idx: number) => (
                        <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                          <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                        </div>
                      ))}
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.ticketingReportCrm}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Features */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">{categories.additional}</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.whatsappFlow}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.agentAssignmentWorkload}</div>
                        <div className="text-center">
                          <span className="text-red-500 text-lg">✗</span>
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                      {[features.createCustomGoal, features.editCustomGoal, features.deleteCustomGoal].map((feature: string, idx: number) => (
                        <div key={idx} className="grid grid-cols-5 gap-4 items-center py-2">
                          <div className="text-slate-700 dark:text-slate-300">{feature}</div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <span className="text-red-500 text-lg">✗</span>
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                          <div className="text-center">
                            <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          </div>
                        </div>
                      ))}
                      <div className="grid grid-cols-5 gap-4 items-center py-2">
                        <div className="text-slate-700 dark:text-slate-300">{features.emailOtpMekari}</div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                        <div className="text-center">
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* CTA Footer */}
            <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-800 dark:to-slate-700 rounded-b-2xl border-t dark:border-slate-600">
              <div className="text-center">
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  {t('consultation')}
                </p>
                <Link href="#quotation-section">
                  <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {t('consultationCta')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Accordion View */}
        <div className="lg:hidden">
          <Card className="bg-white dark:bg-slate-800 border dark:border-slate-700">
            <CardHeader className="text-center bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-slate-700">
              <CardTitle className="text-slate-900 dark:text-white">{t('mobile.title')}</CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-400">
                {t('mobile.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Accordion type="single" collapsible className="w-full">
                {/* WhatsApp Business API */}
                <AccordionItem value="whatsapp-api" className="border-b dark:border-slate-700">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline text-slate-900 dark:text-white">
                    <div className="flex items-center">
                      <MessageSquare className="h-5 w-5 mr-3 text-blue-600" />
                      {categories.whatsappApi}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-4 gap-2 text-xs font-medium text-center border-b pb-2">
                        <div className="text-slate-900 dark:text-white">{planNames.broadcast}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.service}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.sales}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.enterprise}</div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.whatsappBlueTick}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.waMasking}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.whatsappCall}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <span className="text-slate-400 text-sm">✗</span>
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.whatsappGroup}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <span className="text-slate-400 text-sm">✗</span>
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Channel Integration */}
                <AccordionItem value="channel-integration" className="border-b dark:border-slate-700">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline text-slate-900 dark:text-white">
                    <div className="flex items-center">
                      <Globe className="h-5 w-5 mr-3 text-blue-600" />
                      {categories.channelIntegration}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-4 gap-2 text-xs font-medium text-center border-b pb-2">
                        <div className="text-slate-900 dark:text-white">{planNames.broadcast}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.service}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.sales}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.enterprise}</div>
                      </div>
                      
                      <div className="space-y-3">
                        {[features.instagram, features.facebook, features.line, features.twitter, features.telegram, features.googleMyBusiness].map((channel: string, idx: number) => (
                          <div key={idx}>
                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{channel}</div>
                            <div className="grid grid-cols-4 gap-2 text-center">
                              <span className="text-slate-400 text-sm">✗</span>
                              <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                              <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                              <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            </div>
                          </div>
                        ))}
                        
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.ecommerce}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <span className="text-slate-400 text-sm">✗</span>
                            <span className="text-xs font-medium text-blue-600">Unlimited</span>
                            <span className="text-xs font-medium text-blue-600">Unlimited</span>
                            <span className="text-xs font-medium text-blue-600">Unlimited</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* AI & Chatbot */}
                <AccordionItem value="chatbot" className="border-b dark:border-slate-700">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline text-slate-900 dark:text-white">
                    <div className="flex items-center">
                      <Bot className="h-5 w-5 mr-3 text-blue-600" />
                      {categories.aiChatbot}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-4 gap-2 text-xs font-medium text-center border-b pb-2">
                        <div className="text-slate-900 dark:text-white">{planNames.broadcast}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.service}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.sales}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.enterprise}</div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.basicChatbot}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <span className="text-slate-400 text-sm">✗</span>
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.commerceChatbot}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <span className="text-slate-400 text-sm">✗</span>
                            <span className="text-slate-400 text-sm">✗</span>
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.nlpIntent}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <span className="text-slate-400 text-sm">✗</span>
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{features.conversationalAi}</div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <span className="text-slate-400 text-sm">✗</span>
                            <span className="text-slate-400 text-sm">✗</span>
                            <span className="text-slate-400 text-sm">✗</span>
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* CRM & Sales */}
                <AccordionItem value="crm-sales">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline text-slate-900 dark:text-white">
                    <div className="flex items-center">
                      <BarChart3 className="h-5 w-5 mr-3 text-blue-600" />
                      {categories.crmSales}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-4 gap-2 text-xs font-medium text-center border-b pb-2">
                        <div className="text-slate-900 dark:text-white">{planNames.broadcast}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.service}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.sales}</div>
                        <div className="text-slate-900 dark:text-white">{planNames.enterprise}</div>
                      </div>
                      
                      <div className="space-y-3">
                        {[features.dealPipeline, features.gpsLiveTracking, features.addTask, features.createCustomGoal, features.advancedRulesApprover].map((feature: string, idx: number) => (
                          <div key={idx}>
                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{feature}</div>
                            <div className="grid grid-cols-4 gap-2 text-center">
                              <span className="text-slate-400 text-sm">✗</span>
                              <span className="text-slate-400 text-sm">✗</span>
                              <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                              <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
            
            <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-800 dark:to-slate-700 border-t dark:border-slate-600">
              <div className="text-center">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {t('mobile.help')}
                </p>
                <Link href="#quotation-section" target="_blank">
                  <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {t('consultationCta')}
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PriceComparisonSection;